import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tabs,
    Tab,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    alpha,
    Grid,
    Chip,
    TableSortLabel,
    Skeleton,
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    Delete as DeleteIcon,
    RestoreFromTrash as RestoreIcon,
    DirectionsRun as DirectionsRunIcon,
    EmojiEvents as EmojiEventsIcon,
    ArrowBack as ArrowBackIcon,
    ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    getTrackEvents,
    getPersonalRecords,
    deleteTrackEvent,
    getArchivedTrackEvents,
    restoreTrackEvent,
    deduplicateTrackEvents,
} from 'api/TrackEvents';
import { useSnackbar } from 'notistack';
import useTheme from '@mui/system/useTheme';
import { formatTrackEventResult, normalizeTrackEventName } from 'utils';

interface TrackEventViewerProps {
    open: boolean;
    onClose: () => void;
    group: any;
    initialAthlete?: string;
    isOrgAdmin?: boolean;
    members?: any[];
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} id={`viewer-tabpanel-${index}`} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const TrackEventViewer: React.FC<TrackEventViewerProps> = ({
    open,
    onClose,
    group,
    initialAthlete,
    isOrgAdmin = false,
    members: membersProp,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [currentTab, setCurrentTab] = useState(0);
    const [loading, setLoading] = useState(false);

    const glassStyle = {
        background: alpha(theme.palette.background.paper, 0.6),
        backdropFilter: 'blur(20px)',
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.1)}`,
    };

    // Normalize event names using shared utility that handles
    // cross-scraper variations (e.g. "Boys 60 meter Dash" ↔ "60 Meter")
    const normalizeEventName = (eventName: string): string => {
        if (!eventName) return '';
        return normalizeTrackEventName(eventName);
    };

    // Personal Records
    const [personalRecords, setPersonalRecords] = useState<any[]>([]);

    // Sorting state for Personal Records
    type SortColumn = 'athlete' | 'event' | 'result' | 'meet' | 'date';
    const [sortColumn, setSortColumn] = useState<SortColumn>('athlete');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Date range helper
    type DateRangePreset = '3m' | '6m' | '1y' | 'custom';
    const getDateRange = (preset: DateRangePreset): { from: string; to: string } => {
        const today = new Date();
        const to = today.toISOString().split('T')[0];
        const from = new Date(today);
        switch (preset) {
            case '3m':
                from.setMonth(from.getMonth() - 3);
                break;
            case '6m':
                from.setMonth(from.getMonth() - 6);
                break;
            case '1y':
                from.setFullYear(from.getFullYear() - 1);
                break;
            case 'custom':
                return { from: '', to: '' };
        }
        return { from: from.toISOString().split('T')[0], to };
    };

    // Performance Log
    const [performances, setPerformances] = useState<any[]>([]);
    const [selectedAthlete, setSelectedAthlete] = useState<string>('all');
    const [selectedEvent, setSelectedEvent] = useState<string>('all');
    const [dateRangePreset, setDateRangePreset] = useState<DateRangePreset>('3m');
    const [dateFrom, setDateFrom] = useState<string>(() => getDateRange('3m').from);
    const [dateTo, setDateTo] = useState<string>(() => getDateRange('3m').to);

    const handleDatePresetChange = (preset: DateRangePreset) => {
        setDateRangePreset(preset);
        if (preset !== 'custom') {
            const range = getDateRange(preset);
            setDateFrom(range.from);
            setDateTo(range.to);
        }
    };

    // Athlete Trends
    const [trendAthlete, setTrendAthlete] = useState<string>('');
    const [athleteTrends, setAthleteTrends] = useState<any>({});
    const [loadingTrends, setLoadingTrends] = useState(false);

    // Event Detail Drill-Down (when a PR tile is clicked)
    const [eventDetailOpen, setEventDetailOpen] = useState(false);
    const [eventDetailEvent, setEventDetailEvent] = useState<string>('');
    const [eventDetailPR, setEventDetailPR] = useState<any>(null);
    const [eventDetailData, setEventDetailData] = useState<any[]>([]);
    const [eventDetailLoading, setEventDetailLoading] = useState(false);

    // Athletes list for dropdown
    const [athletes, setAthletes] = useState<any[]>([]);

    // Events list for dropdown
    const [events, setEvents] = useState<string[]>([]);

    // Manage Data (admin-only)
    const [archivedEvents, setArchivedEvents] = useState<any[]>([]);
    const [loadingArchived, setLoadingArchived] = useState(false);

    // Inline archive confirmation
    const [confirmingArchive, setConfirmingArchive] = useState<string | null>(null);

    // Deduplication
    const [dedupPreview, setDedupPreview] = useState<any>(null);
    const [dedupLoading, setDedupLoading] = useState(false);

    // Tab index for admin: 0=PRs, 1=Perf Log, 2=Trends, 3=Manage Data
    const MANAGE_DATA_TAB = 3;

    const handlePRTileClick = useCallback(
        async (eventType: string, prResult: any) => {
            setEventDetailEvent(eventType);
            setEventDetailPR(prResult);
            setEventDetailOpen(true);
            setEventDetailLoading(true);

            try {
                // Load all performances for this athlete (we filter by event on client)
                const detailFilters: any = {
                    'filter.individual': trendAthlete || initialAthlete,
                    limit: 100,
                };
                if (group?.uuid) {
                    detailFilters['filter.group'] = group.uuid;
                }
                const response = await getTrackEvents(detailFilters);

                const filtered = (response.data.data || [])
                    .filter((perf: any) => parseFloat(perf.result_value) > 0)
                    .filter((perf: any) => normalizeEventName(perf.event_type) === eventType);

                // Deduplicate by date + normalized result
                const seen = new Set<string>();
                const allPerfs = filtered
                    .filter((perf: any) => {
                        const val =
                            perf.result_unit === 'm'
                                ? (parseFloat(perf.result_value) * 39.3701).toFixed(2)
                                : parseFloat(perf.result_value).toFixed(2);
                        const d = perf.meet_date.split('T')[0];
                        const key = `${d}|${val}`;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    })
                    .sort((a: any, b: any) => {
                        const dateA = new Date(a.meet_date).getTime();
                        const dateB = new Date(b.meet_date).getTime();
                        return dateB - dateA;
                    });

                setEventDetailData(allPerfs);
            } catch (err) {
                enqueueSnackbar('Failed to load event details', {
                    variant: 'error',
                });
            } finally {
                setEventDetailLoading(false);
            }
        },
        [group?.uuid, initialAthlete, trendAthlete, enqueueSnackbar],
    );

    const isPRRow = (perf: any): boolean => {
        if (!eventDetailPR) return false;
        return (
            parseFloat(perf.result_value) === eventDetailPR.result &&
            new Date(perf.meet_date).toISOString().split('T')[0] ===
                new Date(eventDetailPR.date).toISOString().split('T')[0]
        );
    };

    const getSourceLabel = (source: string | null | undefined): string => {
        if (!source) return 'Manual';
        switch (source) {
            case 'milesplit':
                return 'MileSplit';
            case 'athletic_net':
                return 'Athletic.net';
            default:
                return source;
        }
    };

    const getSourceColor = (
        source: string | null | undefined,
    ): 'default' | 'primary' | 'secondary' | 'info' | 'success' => {
        if (!source) return 'default';
        switch (source) {
            case 'milesplit':
                return 'primary';
            case 'athletic_net':
                return 'info';
            default:
                return 'default';
        }
    };

    const loadData = useCallback(async () => {
        if (!group?.uuid && !initialAthlete) return;
        try {
            setLoading(true);

            // Load PRs — filter by group when available, otherwise by individual
            const prFilters: any = {};
            if (group?.uuid) {
                prFilters['filter.group'] = group.uuid;
            }
            if (initialAthlete) {
                prFilters['filter.individual'] = [initialAthlete];
            }
            const prsResponse = await getPersonalRecords(prFilters);
            // Merge PRs that are the same event under different names
            // e.g. "Boys 60 meter Dash" (Athletic.net) and "60 Meter" (MileSplit)
            const rawPRs: any[] = prsResponse.data.personal_records || [];
            const mergedMap = new Map<string, any>();

            // Normalize a result value to inches if unit is 'm'
            const toComparable = (val: number, unit: string) =>
                unit === 'm' ? val * 39.3701 : val;

            for (const pr of rawPRs) {
                const key = `${pr.individual_uuid}|${normalizeEventName(pr.event_type)}`;
                const existing = mergedMap.get(key);
                if (!existing) {
                    mergedMap.set(key, {
                        ...pr,
                        event_type: normalizeEventName(pr.event_type),
                        // Normalize m → in so display is consistent
                        best_result:
                            pr.result_unit === 'm'
                                ? (parseFloat(pr.best_result) * 39.3701).toFixed(2)
                                : pr.best_result,
                        result_unit: pr.result_unit === 'm' ? 'in' : pr.result_unit,
                        event_count: pr.event_count || 1,
                    });
                } else {
                    const timeBased = existing.result_unit === 's';
                    const curr = toComparable(
                        parseFloat(existing.best_result),
                        existing.result_unit,
                    );
                    const next = toComparable(parseFloat(pr.best_result), pr.result_unit);
                    const isBetter = timeBased ? next < curr : next > curr;
                    const totalCount = (existing.event_count || 1) + (pr.event_count || 1);
                    if (isBetter) {
                        mergedMap.set(key, {
                            ...pr,
                            event_type: normalizeEventName(pr.event_type),
                            best_result:
                                pr.result_unit === 'm'
                                    ? (parseFloat(pr.best_result) * 39.3701).toFixed(2)
                                    : pr.best_result,
                            result_unit: pr.result_unit === 'm' ? 'in' : pr.result_unit,
                            event_count: totalCount,
                        });
                    } else {
                        existing.event_count = totalCount;
                    }
                }
            }

            const mergedPRs = Array.from(mergedMap.values());
            setPersonalRecords(mergedPRs);

            // Extract unique athletes and events from merged data
            const uniqueAthletes = Array.from(
                new Set(mergedPRs.map((pr: any) => pr.individual_uuid)),
            ).map((uuid) => {
                const pr = mergedPRs.find((p: any) => p.individual_uuid === uuid);
                return {
                    uuid,
                    name: pr?.individual_name || 'Unknown',
                };
            });
            setAthletes(uniqueAthletes);

            const uniqueEvents = Array.from(
                new Set(mergedPRs.map((pr: any) => pr.event_type)),
            ).sort();
            setEvents(uniqueEvents);
        } catch (err: any) {
            enqueueSnackbar('Failed to load performance data', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [group?.uuid, initialAthlete, enqueueSnackbar]);

    const loadPerformances = useCallback(async () => {
        if (!group?.uuid && !initialAthlete) return;
        try {
            setLoading(true);

            const filters: any = {
                limit: 500, // Backend max is 500
            };

            if (group?.uuid) {
                filters['filter.group'] = group.uuid;
            }

            if (initialAthlete) {
                filters['filter.individual'] = initialAthlete;
            } else if (selectedAthlete !== 'all') {
                filters['filter.individual'] = selectedAthlete;
            }

            // Don't send event filter to backend - we'll filter on frontend by normalized name
            // if (selectedEvent !== 'all') {
            //     filters['filter.event_type'] = selectedEvent;
            // }

            if (dateFrom) {
                filters['filter.date_from'] = dateFrom;
            }

            if (dateTo) {
                filters['filter.date_to'] = dateTo;
            }

            const response = await getTrackEvents(filters);
            let allPerformances = response.data.data || [];

            // Filter by normalized event name on the frontend
            if (selectedEvent !== 'all') {
                allPerformances = allPerformances.filter((perf: any) => {
                    return normalizeEventName(perf.event_type) === selectedEvent;
                });
            }

            setPerformances(allPerformances);
        } catch (err: any) {
            enqueueSnackbar('Failed to load performances', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [
        group?.uuid,
        initialAthlete,
        selectedAthlete,
        selectedEvent,
        dateFrom,
        dateTo,
        enqueueSnackbar,
    ]);

    const loadAthleteTrends = useCallback(
        async (athleteUuid: string) => {
            if (!athleteUuid) return;
            if (!group?.uuid && !initialAthlete) return;

            try {
                setLoadingTrends(true);

                const trendFilters: any = {
                    'filter.individual': athleteUuid,
                    limit: 100,
                };
                if (group?.uuid) {
                    trendFilters['filter.group'] = group.uuid;
                }

                const response = await getTrackEvents(trendFilters);

                // Group by normalized event_type and sort by date, removing duplicates based on unique meet+date+result+location
                const groupedByEvent = response.data.data.reduce((acc: any, perf: any) => {
                    // Skip DNS/DNF/scratch results stored as 0
                    if (parseFloat(perf.result_value) <= 0) {
                        return acc;
                    }

                    const normalizedEvent = normalizeEventName(perf.event_type);

                    if (!acc[normalizedEvent]) {
                        acc[normalizedEvent] = [];
                    }

                    // Create unique key to detect duplicates
                    // Use date + normalized result only (not meet name, which
                    // can differ between sources for the same performance)
                    const perfDate = perf.meet_date.split('T')[0];
                    const normVal =
                        perf.result_unit === 'm'
                            ? (parseFloat(perf.result_value) * 39.3701).toFixed(2)
                            : parseFloat(perf.result_value).toFixed(2);
                    const uniqueKey = `${perfDate}|${normVal}`;

                    // Check if this exact performance already exists
                    const exists = acc[normalizedEvent].some((p: any) => {
                        const existingDate = new Date(p.date).toISOString().split('T')[0];
                        const existingVal =
                            p.unit === 'm' ? (p.result * 39.3701).toFixed(2) : p.result.toFixed(2);
                        return `${existingDate}|${existingVal}` === uniqueKey;
                    });

                    if (!exists) {
                        acc[normalizedEvent].push({
                            date: new Date(perf.meet_date).getTime(),
                            dateFormatted: new Date(perf.meet_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                            }),
                            result: parseFloat(perf.result_value),
                            meet: perf.meet_name,
                            location: perf.meet_location,
                            unit: perf.result_unit,
                            uuid: perf.uuid,
                        });
                    }
                    return acc;
                }, {});

                // Sort each event's data by date
                Object.keys(groupedByEvent).forEach((event) => {
                    groupedByEvent[event].sort((a: any, b: any) => a.date - b.date);
                });

                setAthleteTrends(groupedByEvent);
            } catch (err) {
                enqueueSnackbar('Failed to load athlete trends', { variant: 'error' });
            } finally {
                setLoadingTrends(false);
            }
        },
        [group?.uuid, initialAthlete, enqueueSnackbar],
    );

    useEffect(() => {
        if (open && initialAthlete) {
            setSelectedAthlete(initialAthlete);
            setTrendAthlete(initialAthlete);
            setCurrentTab(2);
        }
    }, [open, initialAthlete]);

    useEffect(() => {
        if (open && (group?.uuid || initialAthlete)) {
            loadData();
            loadPerformances();
        }
    }, [open, group?.uuid, initialAthlete, loadData, loadPerformances]);

    useEffect(() => {
        if (currentTab === 2 && open && trendAthlete) {
            loadAthleteTrends(trendAthlete);
        }
    }, [currentTab, open, trendAthlete, loadAthleteTrends]);

    const handleSortRequest = (column: SortColumn) => {
        const isAsc = sortColumn === column && sortDirection === 'asc';
        const newDirection = isAsc ? 'desc' : 'asc';
        setSortDirection(newDirection);
        setSortColumn(column);
    };

    const getSortedRecords = (records: any[]) => {
        return [...records].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case 'athlete':
                    aValue = a.individual_name?.toLowerCase() || '';
                    bValue = b.individual_name?.toLowerCase() || '';
                    break;
                case 'event':
                    aValue = normalizeEventName(a.event_type).toLowerCase();
                    bValue = normalizeEventName(b.event_type).toLowerCase();
                    break;
                case 'result':
                    aValue = parseFloat(a.best_result) || 0;
                    bValue = parseFloat(b.best_result) || 0;
                    break;
                case 'meet':
                    aValue = a.meet_name?.toLowerCase() || '';
                    bValue = b.meet_name?.toLowerCase() || '';
                    break;
                case 'date':
                    aValue = a.meet_date ? new Date(a.meet_date).getTime() : 0;
                    bValue = b.meet_date ? new Date(b.meet_date).getTime() : 0;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const loadArchivedEvents = useCallback(async () => {
        if (!group?.uuid && !initialAthlete) return;
        try {
            setLoadingArchived(true);
            const filters: any = { limit: 500 };
            if (group?.uuid) filters['filter.group'] = group.uuid;
            if (initialAthlete) filters['filter.individual'] = initialAthlete;
            const response = await getArchivedTrackEvents(filters);
            setArchivedEvents(response.data.data || []);
        } catch {
            enqueueSnackbar('Failed to load archived events', { variant: 'error' });
        } finally {
            setLoadingArchived(false);
        }
    }, [group?.uuid, initialAthlete, enqueueSnackbar]);

    useEffect(() => {
        if (open && currentTab === MANAGE_DATA_TAB && isOrgAdmin) {
            loadArchivedEvents();
        }
    }, [open, currentTab, isOrgAdmin, loadArchivedEvents]);

    const handleSoftDelete = async (uuid: string) => {
        try {
            setConfirmingArchive(null);
            await deleteTrackEvent(uuid);
            enqueueSnackbar('Event archived', { variant: 'success' });
            loadPerformances();
            loadData();
            if (currentTab === MANAGE_DATA_TAB) loadArchivedEvents();
        } catch {
            enqueueSnackbar('Failed to archive event', { variant: 'error' });
        }
    };

    const handleRestore = async (uuid: string) => {
        try {
            await restoreTrackEvent(uuid);
            enqueueSnackbar('Event restored', { variant: 'success' });
            loadArchivedEvents();
            loadPerformances();
            loadData();
        } catch {
            enqueueSnackbar('Failed to restore event', { variant: 'error' });
        }
    };

    const handleDedupScan = async () => {
        try {
            setDedupLoading(true);
            const filters: any = { dry_run: true };
            if (group?.uuid) filters['filter.group'] = group.uuid;
            if (initialAthlete) filters['filter.individual'] = initialAthlete;
            const response = await deduplicateTrackEvents(filters);
            setDedupPreview(response.data);
        } catch {
            enqueueSnackbar('Failed to scan for duplicates', {
                variant: 'error',
            });
        } finally {
            setDedupLoading(false);
        }
    };

    const handleDedupConfirm = async () => {
        try {
            setDedupLoading(true);
            const filters: any = { dry_run: false };
            if (group?.uuid) filters['filter.group'] = group.uuid;
            if (initialAthlete) filters['filter.individual'] = initialAthlete;
            const response = await deduplicateTrackEvents(filters);
            enqueueSnackbar(`Archived ${response.data.archived_count} duplicate events`, {
                variant: 'success',
            });
            setDedupPreview(null);
            loadPerformances();
            loadData();
            loadArchivedEvents();
        } catch {
            enqueueSnackbar('Failed to deduplicate', { variant: 'error' });
        } finally {
            setDedupLoading(false);
        }
    };

    const handleExportCSV = () => {
        const dataToExport = currentTab === 0 ? personalRecords : performances;

        if (dataToExport.length === 0) {
            enqueueSnackbar('No data to export', { variant: 'warning' });
            return;
        }

        let csv = '';

        if (currentTab === 0) {
            // Export PRs
            csv = 'Athlete,Event,Best Result,Unit,Meet,Date\n';
            personalRecords.forEach((pr) => {
                csv += `"${pr.individual_name}","${normalizeEventName(pr.event_type)}",${
                    pr.best_result
                },"${pr.result_unit}","${pr.meet_name || ''}","${pr.meet_date || ''}"\n`;
            });
        } else {
            // Export performances
            csv = 'Athlete,Event,Result,Unit,Meet,Date,Location,School,Year,Notes\n';
            performances.forEach((perf) => {
                csv += `"${perf.individual?.name || 'Unknown'}","${normalizeEventName(
                    perf.event_type,
                )}",${perf.result_value},"${perf.result_unit}","${perf.meet_name || ''}","${
                    perf.meet_date || ''
                }","${perf.meet_location || ''}","${perf.school || ''}","${perf.year || ''}","${
                    perf.notes || ''
                }"\n`;
            });
        }

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `track_${currentTab === 0 ? 'prs' : 'performances'}_${
            new Date().toISOString().split('T')[0]
        }.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        enqueueSnackbar('Exported successfully', { variant: 'success' });
    };

    const formatResult = formatTrackEventResult;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: glassStyle,
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" position="relative" minHeight={48}>
                        {/* Centered athlete name + photo */}
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={1.5}
                            sx={{
                                position: 'absolute',
                                left: 0,
                                right: 0,
                                pointerEvents: 'none',
                            }}
                        >
                            {(() => {
                                const athleteUuid =
                                    selectedAthlete !== 'all' ? selectedAthlete : initialAthlete;
                                const athlete = athleteUuid
                                    ? athletes.find((a) => a.uuid === athleteUuid)
                                    : null;
                                const member =
                                    athleteUuid && membersProp
                                        ? membersProp.find(
                                              (m) => m.individual?.uuid === athleteUuid,
                                          )
                                        : null;
                                const photo =
                                    member?.individual?.image_urls?.['200'] ||
                                    member?.individual?.image_urls?.avatar ||
                                    member?.image_urls?.avatar ||
                                    null;
                                return (
                                    <>
                                        {athlete && photo && (
                                            <Box
                                                component="img"
                                                src={photo}
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    borderRadius: '50%',
                                                    objectFit: 'cover',
                                                }}
                                            />
                                        )}
                                        <Typography
                                            variant="h5"
                                            fontWeight={700}
                                            textAlign="center"
                                        >
                                            {athlete
                                                ? `${athlete.name} — Track & Field`
                                                : 'Track & Field Performances'}
                                        </Typography>
                                    </>
                                );
                            })()}
                        </Box>
                        {/* Right-side actions */}
                        <Box display="flex" alignItems="center" ml="auto" zIndex={1}>
                            <Button
                                startIcon={<DownloadIcon />}
                                onClick={handleExportCSV}
                                sx={{ mr: 1 }}
                            >
                                Export CSV
                            </Button>
                            <IconButton onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent dividers>
                    {/* Tabs */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tabs value={currentTab} onChange={handleTabChange}>
                            <Tab label={`Personal Records (${personalRecords.length})`} />
                            <Tab label={`Performance Log (${performances.length})`} />
                            <Tab label="Athlete Trends" />
                            {isOrgAdmin && <Tab label="Manage Data" />}
                        </Tabs>
                    </Box>

                    {/* Personal Records Tab */}
                    <TabPanel value={currentTab} index={0}>
                        {/* Athlete Filter */}
                        <Box display="flex" gap={2} mb={3}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Athlete</InputLabel>
                                <Select
                                    value={selectedAthlete}
                                    onChange={(e) => setSelectedAthlete(e.target.value)}
                                    label="Athlete"
                                >
                                    <MenuItem value="all">All Athletes</MenuItem>
                                    {athletes.map((athlete) => (
                                        <MenuItem key={athlete.uuid} value={athlete.uuid}>
                                            {athlete.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Event</InputLabel>
                                <Select
                                    value={selectedEvent}
                                    onChange={(e) => setSelectedEvent(e.target.value)}
                                    label="Event"
                                >
                                    <MenuItem value="all">All Events</MenuItem>
                                    {events.map((event) => (
                                        <MenuItem key={event} value={event}>
                                            {event}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {loading && personalRecords.length === 0 ? (
                            <Box>
                                <Skeleton variant="rounded" height={300} sx={{ borderRadius: 2 }} />
                            </Box>
                        ) : !loading && personalRecords.length === 0 ? (
                            <Alert severity="info">No personal records found</Alert>
                        ) : (
                            <Box sx={{ position: 'relative' }}>
                                {loading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            bgcolor: 'rgba(255,255,255,0.6)',
                                            zIndex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                                <TableContainer component={Paper} variant="outlined">
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell
                                                    sortDirection={
                                                        sortColumn === 'athlete'
                                                            ? sortDirection
                                                            : false
                                                    }
                                                >
                                                    <TableSortLabel
                                                        active={sortColumn === 'athlete'}
                                                        direction={
                                                            sortColumn === 'athlete'
                                                                ? sortDirection
                                                                : 'asc'
                                                        }
                                                        onClick={() => handleSortRequest('athlete')}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <strong>Athlete</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell
                                                    sortDirection={
                                                        sortColumn === 'event'
                                                            ? sortDirection
                                                            : false
                                                    }
                                                >
                                                    <TableSortLabel
                                                        active={sortColumn === 'event'}
                                                        direction={
                                                            sortColumn === 'event'
                                                                ? sortDirection
                                                                : 'asc'
                                                        }
                                                        onClick={() => handleSortRequest('event')}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <strong>Event</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell
                                                    sortDirection={
                                                        sortColumn === 'result'
                                                            ? sortDirection
                                                            : false
                                                    }
                                                >
                                                    <TableSortLabel
                                                        active={sortColumn === 'result'}
                                                        direction={
                                                            sortColumn === 'result'
                                                                ? sortDirection
                                                                : 'asc'
                                                        }
                                                        onClick={() => handleSortRequest('result')}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <strong>Best Result</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell
                                                    sortDirection={
                                                        sortColumn === 'meet'
                                                            ? sortDirection
                                                            : false
                                                    }
                                                >
                                                    <TableSortLabel
                                                        active={sortColumn === 'meet'}
                                                        direction={
                                                            sortColumn === 'meet'
                                                                ? sortDirection
                                                                : 'asc'
                                                        }
                                                        onClick={() => handleSortRequest('meet')}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <strong>Meet</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell
                                                    sortDirection={
                                                        sortColumn === 'date'
                                                            ? sortDirection
                                                            : false
                                                    }
                                                >
                                                    <TableSortLabel
                                                        active={sortColumn === 'date'}
                                                        direction={
                                                            sortColumn === 'date'
                                                                ? sortDirection
                                                                : 'asc'
                                                        }
                                                        onClick={() => handleSortRequest('date')}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            userSelect: 'none',
                                                        }}
                                                    >
                                                        <strong>Date</strong>
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <strong>Records</strong>
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getSortedRecords(
                                                personalRecords.filter(
                                                    (pr) =>
                                                        (selectedAthlete === 'all' ||
                                                            pr.individual_uuid ===
                                                                selectedAthlete) &&
                                                        (selectedEvent === 'all' ||
                                                            normalizeEventName(pr.event_type) ===
                                                                selectedEvent),
                                                ),
                                            ).map((pr, index) => (
                                                <TableRow
                                                    key={index}
                                                    hover
                                                    sx={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        // Switch to Athlete Trends tab and drill into this event
                                                        setTrendAthlete(pr.individual_uuid);
                                                        setCurrentTab(2);
                                                    }}
                                                >
                                                    <TableCell>{pr.individual_name}</TableCell>
                                                    <TableCell>
                                                        {normalizeEventName(pr.event_type)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight="bold"
                                                        >
                                                            {formatResult(
                                                                pr.best_result,
                                                                pr.result_unit,
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{pr.meet_name || '—'}</TableCell>
                                                    <TableCell>
                                                        {pr.meet_date
                                                            ? new Date(
                                                                  pr.meet_date,
                                                              ).toLocaleDateString()
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <Chip
                                                            label={`${pr.event_count || 1}`}
                                                            size="small"
                                                            color="info"
                                                            variant="outlined"
                                                            sx={{ fontSize: 11 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Performance Log Tab */}
                    <TabPanel value={currentTab} index={1}>
                        {/* Filters */}
                        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Athlete</InputLabel>
                                <Select
                                    value={selectedAthlete}
                                    onChange={(e) => setSelectedAthlete(e.target.value)}
                                    label="Athlete"
                                >
                                    <MenuItem value="all">All Athletes</MenuItem>
                                    {athletes.map((athlete) => (
                                        <MenuItem key={athlete.uuid} value={athlete.uuid}>
                                            {athlete.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Event</InputLabel>
                                <Select
                                    value={selectedEvent}
                                    onChange={(e) => setSelectedEvent(e.target.value)}
                                    label="Event"
                                >
                                    <MenuItem value="all">All Events</MenuItem>
                                    {events.map((event) => (
                                        <MenuItem key={event} value={event}>
                                            {event}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                                {(
                                    [
                                        { value: '3m', label: 'Past 3 Months' },
                                        { value: '6m', label: 'Past 6 Months' },
                                        { value: '1y', label: 'Past Year' },
                                        { value: 'custom', label: 'Custom' },
                                    ] as { value: DateRangePreset; label: string }[]
                                ).map((opt) => (
                                    <Chip
                                        key={opt.value}
                                        label={opt.label}
                                        onClick={() => handleDatePresetChange(opt.value)}
                                        color={
                                            dateRangePreset === opt.value ? 'primary' : 'default'
                                        }
                                        variant={
                                            dateRangePreset === opt.value ? 'filled' : 'outlined'
                                        }
                                        sx={{ fontWeight: 600, fontSize: '0.8rem' }}
                                    />
                                ))}
                            </Box>

                            {dateRangePreset === 'custom' && (
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <TextField
                                        label="From Date"
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 150 }}
                                    />
                                    <TextField
                                        label="To Date"
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 150 }}
                                    />
                                </Box>
                            )}

                            {(selectedAthlete !== 'all' ||
                                selectedEvent !== 'all' ||
                                dateRangePreset !== '3m') && (
                                <Button
                                    onClick={() => {
                                        setSelectedAthlete('all');
                                        setSelectedEvent('all');
                                        handleDatePresetChange('3m');
                                    }}
                                >
                                    Clear Filters
                                </Button>
                            )}
                        </Box>

                        {loading && performances.length === 0 ? (
                            <Box>
                                {[1, 2, 3].map((i) => (
                                    <Skeleton
                                        key={i}
                                        variant="rounded"
                                        height={200}
                                        sx={{ mb: 3, borderRadius: 2 }}
                                    />
                                ))}
                            </Box>
                        ) : !loading && performances.length === 0 ? (
                            <Alert severity="info">No performances found</Alert>
                        ) : (
                            <Box sx={{ position: 'relative' }}>
                                {loading && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            bgcolor: 'rgba(255,255,255,0.6)',
                                            zIndex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                                {(() => {
                                    const grouped = performances.reduce(
                                        (acc: Record<string, any[]>, perf: any) => {
                                            const eventName = normalizeEventName(perf.event_type);
                                            if (!acc[eventName]) acc[eventName] = [];
                                            acc[eventName].push(perf);
                                            return acc;
                                        },
                                        {},
                                    );

                                    return Object.entries(grouped).map(
                                        ([eventName, eventPerfs]) => {
                                            const sorted = [...eventPerfs].sort(
                                                (a, b) =>
                                                    new Date(a.meet_date).getTime() -
                                                    new Date(b.meet_date).getTime(),
                                            );

                                            const validPerfs = sorted.filter(
                                                (p) => parseFloat(p.result_value) > 0,
                                            );

                                            const timeBased = validPerfs[0]?.result_unit === 's';
                                            const bestPerf = validPerfs.reduce(
                                                (best: any, curr: any) => {
                                                    if (!best) return curr;
                                                    const bv = parseFloat(best.result_value);
                                                    const cv = parseFloat(curr.result_value);
                                                    return timeBased
                                                        ? cv < bv
                                                            ? curr
                                                            : best
                                                        : cv > bv
                                                        ? curr
                                                        : best;
                                                },
                                                null,
                                            );

                                            const chartData = sorted
                                                .filter((p) => parseFloat(p.result_value) > 0)
                                                .map((p) => ({
                                                    dateFormatted: new Date(
                                                        p.meet_date,
                                                    ).toLocaleDateString('en-US', {
                                                        month: 'short',
                                                        day: 'numeric',
                                                        year: 'numeric',
                                                    }),
                                                    result: parseFloat(p.result_value),
                                                    meet: p.meet_name || 'Unknown Meet',
                                                    location: p.meet_location || '',
                                                    unit: p.result_unit,
                                                }));

                                            return (
                                                <Box
                                                    key={eventName}
                                                    sx={{
                                                        ...glassStyle,
                                                        p: 3,
                                                        mb: 3,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            mb: 3,
                                                        }}
                                                    >
                                                        <Typography variant="h6">
                                                            {eventName}
                                                        </Typography>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                gap: 1,
                                                            }}
                                                        >
                                                            {bestPerf && (
                                                                <Chip
                                                                    label={`PR: ${formatResult(
                                                                        bestPerf.result_value,
                                                                        bestPerf.result_unit,
                                                                    )}`}
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                            )}
                                                            <Chip
                                                                label={`${
                                                                    eventPerfs.length
                                                                } performance${
                                                                    eventPerfs.length !== 1
                                                                        ? 's'
                                                                        : ''
                                                                }`}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </Box>
                                                    </Box>

                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <Box
                                                                sx={{
                                                                    border: `1px solid ${alpha(
                                                                        theme.palette.divider,
                                                                        0.2,
                                                                    )}`,
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    background: alpha(
                                                                        theme.palette.background
                                                                            .paper,
                                                                        0.02,
                                                                    ),
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        borderBottom: `1px solid ${alpha(
                                                                            theme.palette.divider,
                                                                            0.1,
                                                                        )}`,
                                                                        background: alpha(
                                                                            theme.palette.background
                                                                                .paper,
                                                                            0.05,
                                                                        ),
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.secondary"
                                                                    >
                                                                        Performance History
                                                                    </Typography>
                                                                </Box>
                                                                <TableContainer
                                                                    component={Paper}
                                                                    sx={{
                                                                        maxHeight: 300,
                                                                        background: 'transparent',
                                                                        boxShadow: 'none',
                                                                    }}
                                                                >
                                                                    <Table
                                                                        size="small"
                                                                        stickyHeader
                                                                    >
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        bgcolor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.8,
                                                                                            ),
                                                                                        backdropFilter:
                                                                                            'blur(10px)',
                                                                                    }}
                                                                                >
                                                                                    Date
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        bgcolor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.8,
                                                                                            ),
                                                                                        backdropFilter:
                                                                                            'blur(10px)',
                                                                                    }}
                                                                                >
                                                                                    Meet
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    align="right"
                                                                                    sx={{
                                                                                        bgcolor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.8,
                                                                                            ),
                                                                                        backdropFilter:
                                                                                            'blur(10px)',
                                                                                    }}
                                                                                >
                                                                                    Result
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {sorted.map((perf) => (
                                                                                <TableRow
                                                                                    key={perf.uuid}
                                                                                    hover
                                                                                    sx={{
                                                                                        '&:nth-of-type(odd)':
                                                                                            {
                                                                                                bgcolor:
                                                                                                    alpha(
                                                                                                        theme
                                                                                                            .palette
                                                                                                            .action
                                                                                                            .hover,
                                                                                                        0.02,
                                                                                                    ),
                                                                                            },
                                                                                        '&:hover': {
                                                                                            bgcolor:
                                                                                                alpha(
                                                                                                    theme
                                                                                                        .palette
                                                                                                        .action
                                                                                                        .hover,
                                                                                                    0.08,
                                                                                                ),
                                                                                        },
                                                                                    }}
                                                                                >
                                                                                    <TableCell>
                                                                                        <Typography variant="body2">
                                                                                            {perf.meet_date
                                                                                                ? new Date(
                                                                                                      perf.meet_date,
                                                                                                  ).toLocaleDateString(
                                                                                                      'en-US',
                                                                                                      {
                                                                                                          month: 'short',
                                                                                                          day: 'numeric',
                                                                                                          year: 'numeric',
                                                                                                      },
                                                                                                  )
                                                                                                : '—'}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Typography variant="body2">
                                                                                            {perf.meet_name ||
                                                                                                'Unknown Meet'}
                                                                                        </Typography>
                                                                                        {perf.meet_location && (
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                color="text.secondary"
                                                                                                display="block"
                                                                                            >
                                                                                                {
                                                                                                    perf.meet_location
                                                                                                }
                                                                                            </Typography>
                                                                                        )}
                                                                                    </TableCell>
                                                                                    <TableCell align="right">
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            fontWeight="bold"
                                                                                        >
                                                                                            {formatResult(
                                                                                                perf.result_value,
                                                                                                perf.result_unit,
                                                                                            )}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </Box>
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <Box
                                                                sx={{
                                                                    border: `1px solid ${alpha(
                                                                        theme.palette.divider,
                                                                        0.2,
                                                                    )}`,
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    background: alpha(
                                                                        theme.palette.background
                                                                            .paper,
                                                                        0.02,
                                                                    ),
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        borderBottom: `1px solid ${alpha(
                                                                            theme.palette.divider,
                                                                            0.1,
                                                                        )}`,
                                                                        background: alpha(
                                                                            theme.palette.background
                                                                                .paper,
                                                                            0.05,
                                                                        ),
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.secondary"
                                                                    >
                                                                        Trend Analysis
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ p: 2 }}>
                                                                    {chartData.length > 1 ? (
                                                                        <ResponsiveContainer
                                                                            width="100%"
                                                                            height={300}
                                                                        >
                                                                            <LineChart
                                                                                data={chartData}
                                                                            >
                                                                                <CartesianGrid
                                                                                    strokeDasharray="3 3"
                                                                                    stroke={alpha(
                                                                                        theme
                                                                                            .palette
                                                                                            .divider,
                                                                                        0.2,
                                                                                    )}
                                                                                />
                                                                                <XAxis
                                                                                    dataKey="dateFormatted"
                                                                                    stroke={
                                                                                        theme
                                                                                            .palette
                                                                                            .text
                                                                                            .secondary
                                                                                    }
                                                                                    style={{
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                />
                                                                                <YAxis
                                                                                    stroke={
                                                                                        theme
                                                                                            .palette
                                                                                            .text
                                                                                            .secondary
                                                                                    }
                                                                                    style={{
                                                                                        fontSize: 12,
                                                                                    }}
                                                                                    label={{
                                                                                        value:
                                                                                            chartData[0]
                                                                                                ?.unit ||
                                                                                            's',
                                                                                        angle: -90,
                                                                                        position:
                                                                                            'insideLeft',
                                                                                        style: {
                                                                                            fill: theme
                                                                                                .palette
                                                                                                .text
                                                                                                .secondary,
                                                                                            fontSize: 12,
                                                                                        },
                                                                                    }}
                                                                                />
                                                                                <Tooltip
                                                                                    contentStyle={{
                                                                                        backgroundColor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.95,
                                                                                            ),
                                                                                        border: `1px solid ${alpha(
                                                                                            theme
                                                                                                .palette
                                                                                                .divider,
                                                                                            0.2,
                                                                                        )}`,
                                                                                        borderRadius: 8,
                                                                                        padding:
                                                                                            '12px',
                                                                                    }}
                                                                                    content={({
                                                                                        active,
                                                                                        payload,
                                                                                    }) => {
                                                                                        if (
                                                                                            active &&
                                                                                            payload &&
                                                                                            payload.length
                                                                                        ) {
                                                                                            const p =
                                                                                                payload[0]
                                                                                                    .payload;
                                                                                            return (
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        bgcolor:
                                                                                                            alpha(
                                                                                                                theme
                                                                                                                    .palette
                                                                                                                    .background
                                                                                                                    .paper,
                                                                                                                0.95,
                                                                                                            ),
                                                                                                        p: 1.5,
                                                                                                        borderRadius: 1,
                                                                                                        border: `1px solid ${alpha(
                                                                                                            theme
                                                                                                                .palette
                                                                                                                .divider,
                                                                                                            0.2,
                                                                                                        )}`,
                                                                                                    }}
                                                                                                >
                                                                                                    <Typography
                                                                                                        variant="body2"
                                                                                                        fontWeight="bold"
                                                                                                    >
                                                                                                        {
                                                                                                            p.dateFormatted
                                                                                                        }
                                                                                                    </Typography>
                                                                                                    <Typography
                                                                                                        variant="body2"
                                                                                                        mt={
                                                                                                            0.5
                                                                                                        }
                                                                                                    >
                                                                                                        {
                                                                                                            p.meet
                                                                                                        }
                                                                                                    </Typography>
                                                                                                    {p.location && (
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            color="text.secondary"
                                                                                                            display="block"
                                                                                                        >
                                                                                                            {
                                                                                                                p.location
                                                                                                            }
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                    <Typography
                                                                                                        variant="body2"
                                                                                                        fontWeight="bold"
                                                                                                        color="primary"
                                                                                                        mt={
                                                                                                            0.5
                                                                                                        }
                                                                                                    >
                                                                                                        Result:{' '}
                                                                                                        {formatResult(
                                                                                                            p.result,
                                                                                                            p.unit,
                                                                                                        )}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            );
                                                                                        }
                                                                                        return null;
                                                                                    }}
                                                                                />
                                                                                <Line
                                                                                    type="monotone"
                                                                                    dataKey="result"
                                                                                    stroke={
                                                                                        theme
                                                                                            .palette
                                                                                            .primary
                                                                                            .main
                                                                                    }
                                                                                    strokeWidth={2}
                                                                                    dot={{
                                                                                        fill: theme
                                                                                            .palette
                                                                                            .primary
                                                                                            .main,
                                                                                        r: 4,
                                                                                    }}
                                                                                    activeDot={{
                                                                                        r: 6,
                                                                                        fill: theme
                                                                                            .palette
                                                                                            .primary
                                                                                            .main,
                                                                                    }}
                                                                                />
                                                                            </LineChart>
                                                                        </ResponsiveContainer>
                                                                    ) : (
                                                                        <Box
                                                                            display="flex"
                                                                            justifyContent="center"
                                                                            alignItems="center"
                                                                            height={300}
                                                                        >
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                            >
                                                                                Need 2+ data points
                                                                                for trend chart
                                                                            </Typography>
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            );
                                        },
                                    );
                                })()}
                            </Box>
                        )}
                    </TabPanel>

                    {/* Athlete Trends Tab */}
                    <TabPanel value={currentTab} index={2}>
                        {/* Athlete Selector */}
                        <Box sx={{ mb: 3 }}>
                            <FormControl sx={{ minWidth: 300 }}>
                                <InputLabel>Select Athlete</InputLabel>
                                <Select
                                    value={trendAthlete}
                                    onChange={(e) => setTrendAthlete(e.target.value)}
                                    label="Select Athlete"
                                >
                                    {athletes.map((athlete) => (
                                        <MenuItem key={athlete.uuid} value={athlete.uuid}>
                                            {athlete.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        {/* Charts */}
                        {!trendAthlete ? (
                            <Alert severity="info">
                                Select an athlete to view their performance trends
                            </Alert>
                        ) : loadingTrends && Object.keys(athleteTrends).length === 0 ? (
                            <Box>
                                {[1, 2, 3].map((i) => (
                                    <Skeleton
                                        key={i}
                                        variant="rounded"
                                        height={200}
                                        sx={{ mb: 3, borderRadius: 2 }}
                                    />
                                ))}
                            </Box>
                        ) : !loadingTrends && Object.keys(athleteTrends).length === 0 ? (
                            <Alert severity="info">
                                No performance data available for this athlete
                            </Alert>
                        ) : (
                            <Box sx={{ position: 'relative' }}>
                                {loadingTrends && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            bottom: 0,
                                            bgcolor: 'rgba(255,255,255,0.6)',
                                            zIndex: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: 2,
                                        }}
                                    >
                                        <CircularProgress />
                                    </Box>
                                )}
                                <>
                                    {/* PR Tiles Grid */}
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                                            {(() => {
                                                const athlete = athletes.find(
                                                    (a) => a.uuid === trendAthlete,
                                                );
                                                const name = athlete?.name || 'Athlete';
                                                const total = Object.values(athleteTrends).reduce(
                                                    (sum: number, events: any) =>
                                                        sum +
                                                        events.filter(
                                                            (d: any) => parseFloat(d.result) > 0,
                                                        ).length,
                                                    0,
                                                );
                                                const eventCount =
                                                    Object.keys(athleteTrends).length;
                                                return `${name} — ${total} performance${
                                                    total !== 1 ? 's' : ''
                                                } across ${eventCount} event${
                                                    eventCount !== 1 ? 's' : ''
                                                }`;
                                            })()}
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {Object.entries(athleteTrends).map(
                                                ([eventType, data]: [string, any]) => {
                                                    // Filter out any 0-value results (DNS/DNF/scratch)
                                                    const validData = data.filter(
                                                        (d: any) => parseFloat(d.result) > 0,
                                                    );
                                                    const bestResult = validData.reduce(
                                                        (best: any, current: any) => {
                                                            const currentVal = parseFloat(
                                                                current.result,
                                                            );
                                                            const bestVal = best
                                                                ? parseFloat(best.result)
                                                                : Infinity;
                                                            return currentVal < bestVal
                                                                ? current
                                                                : best;
                                                        },
                                                        null,
                                                    );

                                                    if (!bestResult) return null;

                                                    return (
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            sm={6}
                                                            md={4}
                                                            key={eventType}
                                                        >
                                                            <Box
                                                                onClick={() =>
                                                                    handlePRTileClick(
                                                                        eventType,
                                                                        bestResult,
                                                                    )
                                                                }
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    background: alpha(
                                                                        theme.palette.background
                                                                            .paper,
                                                                        0.4,
                                                                    ),
                                                                    backdropFilter: 'blur(20px)',
                                                                    borderRadius: 3,
                                                                    border: `2px solid ${alpha(
                                                                        theme.palette.info.main,
                                                                        0.3,
                                                                    )}`,
                                                                    boxShadow: `0 0 20px ${alpha(
                                                                        theme.palette.info.main,
                                                                        0.2,
                                                                    )}, 0 8px 32px ${alpha(
                                                                        theme.palette.common.black,
                                                                        0.1,
                                                                    )}`,
                                                                    p: 3,
                                                                    height: '100%',
                                                                    transition:
                                                                        'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
                                                                    '&:hover': {
                                                                        transform:
                                                                            'translateY(-4px)',
                                                                        borderColor: alpha(
                                                                            theme.palette.info.main,
                                                                            0.5,
                                                                        ),
                                                                        boxShadow: `0 0 30px ${alpha(
                                                                            theme.palette.info.main,
                                                                            0.4,
                                                                        )}, 0 12px 40px ${alpha(
                                                                            theme.palette.common
                                                                                .black,
                                                                            0.2,
                                                                        )}`,
                                                                    },
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent:
                                                                            'space-between',
                                                                        mb: 2,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body1"
                                                                        fontWeight="600"
                                                                    >
                                                                        {eventType}
                                                                    </Typography>
                                                                    <Chip
                                                                        label={`${validData.length}`}
                                                                        size="small"
                                                                        color="info"
                                                                        variant="outlined"
                                                                        sx={{ fontSize: 11 }}
                                                                    />
                                                                </Box>
                                                                <Typography
                                                                    variant="h3"
                                                                    fontWeight="bold"
                                                                    sx={{
                                                                        color: theme.palette.info
                                                                            .main,
                                                                        mb: 1.5,
                                                                        letterSpacing: '-0.02em',
                                                                    }}
                                                                >
                                                                    {formatResult(
                                                                        bestResult.result,
                                                                        bestResult.unit,
                                                                    )}
                                                                </Typography>
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ mb: 0.5 }}
                                                                >
                                                                    {bestResult.dateFormatted}
                                                                </Typography>
                                                                {bestResult.meet && (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                    >
                                                                        {bestResult.meet}
                                                                    </Typography>
                                                                )}
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-end',
                                                                        mt: 1.5,
                                                                        pt: 1,
                                                                        borderTop: `1px solid ${alpha(
                                                                            theme.palette.divider,
                                                                            0.15,
                                                                        )}`,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: theme.palette
                                                                                .info.main,
                                                                            fontWeight: 600,
                                                                        }}
                                                                    >
                                                                        {`See all ${
                                                                            validData.length
                                                                        } meet time${
                                                                            validData.length !== 1
                                                                                ? 's'
                                                                                : ''
                                                                        }`}
                                                                    </Typography>
                                                                    <ChevronRightIcon
                                                                        sx={{
                                                                            fontSize: 18,
                                                                            color: theme.palette
                                                                                .info.main,
                                                                            ml: 0.25,
                                                                        }}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    );
                                                },
                                            )}
                                        </Grid>
                                    </Box>

                                    {/* Event Detail Cards */}
                                    {Object.entries(athleteTrends).map(
                                        ([eventType, data]: [string, any]) => {
                                            // Filter out 0-value results (DNS/DNF/scratch) for PR calc
                                            const validData = data.filter(
                                                (d: any) => parseFloat(d.result) > 0,
                                            );
                                            const bestResult = validData.reduce(
                                                (best: any, current: any) => {
                                                    const currentVal = parseFloat(current.result);
                                                    const bestVal = best
                                                        ? parseFloat(best.result)
                                                        : Infinity;
                                                    return currentVal < bestVal ? current : best;
                                                },
                                                null,
                                            );

                                            return (
                                                <Box
                                                    key={eventType}
                                                    sx={{
                                                        ...glassStyle,
                                                        p: 3,
                                                        mb: 3,
                                                    }}
                                                >
                                                    {/* Event Header */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            mb: 3,
                                                        }}
                                                    >
                                                        <Typography variant="h6">
                                                            {eventType}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            {bestResult && (
                                                                <Chip
                                                                    label={`PR: ${formatResult(
                                                                        bestResult.result,
                                                                        bestResult.unit,
                                                                    )}`}
                                                                    color="success"
                                                                    size="small"
                                                                />
                                                            )}
                                                            <Chip
                                                                label={`${data.length} performance${
                                                                    data.length !== 1 ? 's' : ''
                                                                }`}
                                                                variant="outlined"
                                                                size="small"
                                                            />
                                                        </Box>
                                                    </Box>

                                                    {/* Side-by-side Grid Layout */}
                                                    <Grid container spacing={3}>
                                                        {/* Datatable */}
                                                        <Grid item xs={12} md={6}>
                                                            <Box
                                                                sx={{
                                                                    border: `1px solid ${alpha(
                                                                        theme.palette.divider,
                                                                        0.2,
                                                                    )}`,
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    background: alpha(
                                                                        theme.palette.background
                                                                            .paper,
                                                                        0.02,
                                                                    ),
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        borderBottom: `1px solid ${alpha(
                                                                            theme.palette.divider,
                                                                            0.1,
                                                                        )}`,
                                                                        background: alpha(
                                                                            theme.palette.background
                                                                                .paper,
                                                                            0.05,
                                                                        ),
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.secondary"
                                                                    >
                                                                        Performance History
                                                                    </Typography>
                                                                </Box>
                                                                <TableContainer
                                                                    component={Paper}
                                                                    sx={{
                                                                        maxHeight: 300,
                                                                        background: 'transparent',
                                                                        boxShadow: 'none',
                                                                    }}
                                                                >
                                                                    <Table
                                                                        size="small"
                                                                        stickyHeader
                                                                    >
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        bgcolor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.8,
                                                                                            ),
                                                                                        backdropFilter:
                                                                                            'blur(10px)',
                                                                                    }}
                                                                                >
                                                                                    Date
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        bgcolor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.8,
                                                                                            ),
                                                                                        backdropFilter:
                                                                                            'blur(10px)',
                                                                                    }}
                                                                                >
                                                                                    Meet
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    align="right"
                                                                                    sx={{
                                                                                        bgcolor:
                                                                                            alpha(
                                                                                                theme
                                                                                                    .palette
                                                                                                    .background
                                                                                                    .paper,
                                                                                                0.8,
                                                                                            ),
                                                                                        backdropFilter:
                                                                                            'blur(10px)',
                                                                                    }}
                                                                                >
                                                                                    Result
                                                                                </TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {data.map(
                                                                                (
                                                                                    perf: any,
                                                                                    idx: number,
                                                                                ) => (
                                                                                    <TableRow
                                                                                        key={idx}
                                                                                        hover
                                                                                        sx={{
                                                                                            '&:nth-of-type(odd)':
                                                                                                {
                                                                                                    bgcolor:
                                                                                                        alpha(
                                                                                                            theme
                                                                                                                .palette
                                                                                                                .action
                                                                                                                .hover,
                                                                                                            0.02,
                                                                                                        ),
                                                                                                },
                                                                                            '&:hover':
                                                                                                {
                                                                                                    bgcolor:
                                                                                                        alpha(
                                                                                                            theme
                                                                                                                .palette
                                                                                                                .action
                                                                                                                .hover,
                                                                                                            0.08,
                                                                                                        ),
                                                                                                },
                                                                                        }}
                                                                                    >
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">
                                                                                                {
                                                                                                    perf.dateFormatted
                                                                                                }
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                        <TableCell>
                                                                                            <Typography variant="body2">
                                                                                                {perf.meet ||
                                                                                                    'Unknown Meet'}
                                                                                            </Typography>
                                                                                            {perf.location && (
                                                                                                <Typography
                                                                                                    variant="caption"
                                                                                                    color="text.secondary"
                                                                                                    display="block"
                                                                                                >
                                                                                                    {
                                                                                                        perf.location
                                                                                                    }
                                                                                                </Typography>
                                                                                            )}
                                                                                        </TableCell>
                                                                                        <TableCell align="right">
                                                                                            <Typography
                                                                                                variant="body2"
                                                                                                fontWeight="bold"
                                                                                            >
                                                                                                {formatResult(
                                                                                                    perf.result,
                                                                                                    perf.unit,
                                                                                                )}
                                                                                            </Typography>
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                ),
                                                                            )}
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </Box>
                                                        </Grid>

                                                        {/* Chart */}
                                                        <Grid item xs={12} md={6}>
                                                            <Box
                                                                sx={{
                                                                    border: `1px solid ${alpha(
                                                                        theme.palette.divider,
                                                                        0.2,
                                                                    )}`,
                                                                    borderRadius: 2,
                                                                    overflow: 'hidden',
                                                                    background: alpha(
                                                                        theme.palette.background
                                                                            .paper,
                                                                        0.02,
                                                                    ),
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        px: 2,
                                                                        py: 1,
                                                                        borderBottom: `1px solid ${alpha(
                                                                            theme.palette.divider,
                                                                            0.1,
                                                                        )}`,
                                                                        background: alpha(
                                                                            theme.palette.background
                                                                                .paper,
                                                                            0.05,
                                                                        ),
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="subtitle2"
                                                                        color="text.secondary"
                                                                    >
                                                                        Trend Analysis
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ p: 2 }}>
                                                                    <ResponsiveContainer
                                                                        width="100%"
                                                                        height={300}
                                                                    >
                                                                        <LineChart data={data}>
                                                                            <CartesianGrid
                                                                                strokeDasharray="3 3"
                                                                                stroke={alpha(
                                                                                    theme.palette
                                                                                        .divider,
                                                                                    0.2,
                                                                                )}
                                                                            />
                                                                            <XAxis
                                                                                dataKey="dateFormatted"
                                                                                stroke={
                                                                                    theme.palette
                                                                                        .text
                                                                                        .secondary
                                                                                }
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                }}
                                                                            />
                                                                            <YAxis
                                                                                stroke={
                                                                                    theme.palette
                                                                                        .text
                                                                                        .secondary
                                                                                }
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                }}
                                                                                label={{
                                                                                    value:
                                                                                        data[0]
                                                                                            ?.unit ||
                                                                                        's',
                                                                                    angle: -90,
                                                                                    position:
                                                                                        'insideLeft',
                                                                                    style: {
                                                                                        fill: theme
                                                                                            .palette
                                                                                            .text
                                                                                            .secondary,
                                                                                        fontSize: 12,
                                                                                    },
                                                                                }}
                                                                            />
                                                                            <Tooltip
                                                                                contentStyle={{
                                                                                    backgroundColor:
                                                                                        alpha(
                                                                                            theme
                                                                                                .palette
                                                                                                .background
                                                                                                .paper,
                                                                                            0.95,
                                                                                        ),
                                                                                    border: `1px solid ${alpha(
                                                                                        theme
                                                                                            .palette
                                                                                            .divider,
                                                                                        0.2,
                                                                                    )}`,
                                                                                    borderRadius: 8,
                                                                                    padding: '12px',
                                                                                }}
                                                                                content={({
                                                                                    active,
                                                                                    payload,
                                                                                }) => {
                                                                                    if (
                                                                                        active &&
                                                                                        payload &&
                                                                                        payload.length
                                                                                    ) {
                                                                                        const perf =
                                                                                            payload[0]
                                                                                                .payload;
                                                                                        return (
                                                                                            <Box
                                                                                                sx={{
                                                                                                    bgcolor:
                                                                                                        alpha(
                                                                                                            theme
                                                                                                                .palette
                                                                                                                .background
                                                                                                                .paper,
                                                                                                            0.95,
                                                                                                        ),
                                                                                                    p: 1.5,
                                                                                                    borderRadius: 1,
                                                                                                    border: `1px solid ${alpha(
                                                                                                        theme
                                                                                                            .palette
                                                                                                            .divider,
                                                                                                        0.2,
                                                                                                    )}`,
                                                                                                }}
                                                                                            >
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    fontWeight="bold"
                                                                                                >
                                                                                                    {
                                                                                                        perf.dateFormatted
                                                                                                    }
                                                                                                </Typography>
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    mt={
                                                                                                        0.5
                                                                                                    }
                                                                                                >
                                                                                                    {perf.meet ||
                                                                                                        'Unknown Meet'}
                                                                                                </Typography>
                                                                                                {perf.location && (
                                                                                                    <Typography
                                                                                                        variant="caption"
                                                                                                        color="text.secondary"
                                                                                                        display="block"
                                                                                                    >
                                                                                                        {
                                                                                                            perf.location
                                                                                                        }
                                                                                                    </Typography>
                                                                                                )}
                                                                                                <Typography
                                                                                                    variant="body2"
                                                                                                    fontWeight="bold"
                                                                                                    color="primary"
                                                                                                    mt={
                                                                                                        0.5
                                                                                                    }
                                                                                                >
                                                                                                    Result:{' '}
                                                                                                    {formatResult(
                                                                                                        perf.result,
                                                                                                        perf.unit,
                                                                                                    )}
                                                                                                </Typography>
                                                                                            </Box>
                                                                                        );
                                                                                    }
                                                                                    return null;
                                                                                }}
                                                                            />
                                                                            <Line
                                                                                type="monotone"
                                                                                dataKey="result"
                                                                                stroke={
                                                                                    theme.palette
                                                                                        .primary
                                                                                        .main
                                                                                }
                                                                                strokeWidth={2}
                                                                                dot={{
                                                                                    fill: theme
                                                                                        .palette
                                                                                        .primary
                                                                                        .main,
                                                                                    r: 4,
                                                                                }}
                                                                                activeDot={{
                                                                                    r: 6,
                                                                                    fill: theme
                                                                                        .palette
                                                                                        .primary
                                                                                        .main,
                                                                                }}
                                                                            />
                                                                        </LineChart>
                                                                    </ResponsiveContainer>
                                                                </Box>
                                                            </Box>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            );
                                        },
                                    )}
                                </>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Manage Data Tab (Org Admin only) */}
                    {isOrgAdmin && (
                        <TabPanel value={currentTab} index={MANAGE_DATA_TAB}>
                            {/* Deduplication Section */}
                            <Box
                                sx={{
                                    mb: 4,
                                    p: 2.5,
                                    borderRadius: 2,
                                    background: alpha(theme.palette.warning.main, 0.06),
                                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                                }}
                            >
                                <Box
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    mb={dedupPreview ? 2 : 0}
                                >
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Duplicate Detection
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Scan for events with the same athlete, event, date, and
                                            result. Keeps the oldest, archives the rest.
                                        </Typography>
                                    </Box>
                                    <Box
                                        display="flex"
                                        gap={1}
                                        alignItems="center"
                                        flexShrink={0}
                                        ml={2}
                                    >
                                        {dedupPreview && dedupPreview.total_duplicates > 0 ? (
                                            <>
                                                <Button
                                                    variant="text"
                                                    onClick={() => setDedupPreview(null)}
                                                    sx={{
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="warning"
                                                    onClick={handleDedupConfirm}
                                                    disabled={dedupLoading}
                                                    sx={{
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    {dedupLoading
                                                        ? 'Archiving...'
                                                        : `Archive ${dedupPreview.total_duplicates} Duplicates`}
                                                </Button>
                                            </>
                                        ) : (
                                            <Button
                                                variant="outlined"
                                                color="warning"
                                                onClick={handleDedupScan}
                                                disabled={dedupLoading}
                                                sx={{
                                                    textTransform: 'none',
                                                }}
                                            >
                                                {dedupLoading
                                                    ? 'Scanning...'
                                                    : 'Scan for Duplicates'}
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                {dedupPreview && (
                                    <Box>
                                        {dedupPreview.total_duplicates === 0 ? (
                                            <Alert severity="success" sx={{ mt: 1 }}>
                                                No duplicates found!
                                            </Alert>
                                        ) : (
                                            <>
                                                <Alert severity="warning" sx={{ mb: 2 }}>
                                                    Found{' '}
                                                    <strong>{dedupPreview.total_duplicates}</strong>{' '}
                                                    Duplicates!
                                                </Alert>
                                                <TableContainer
                                                    component={Paper}
                                                    variant="outlined"
                                                    sx={{ mb: 2 }}
                                                >
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>Athlete</TableCell>
                                                                <TableCell>Event</TableCell>
                                                                <TableCell>Result</TableCell>
                                                                <TableCell>Date</TableCell>
                                                                <TableCell>Total Copies</TableCell>
                                                                <TableCell>To Archive</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {dedupPreview.details.map(
                                                                (d: any, i: number) => (
                                                                    <TableRow key={i}>
                                                                        <TableCell>
                                                                            {d.athlete}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {d.event_type}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight="bold"
                                                                            >
                                                                                {formatResult(
                                                                                    d.result_value,
                                                                                    d.result_unit,
                                                                                )}
                                                                            </Typography>
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {d.meet_date
                                                                                ? new Date(
                                                                                      d.meet_date,
                                                                                  ).toLocaleDateString(
                                                                                      'en-US',
                                                                                      {
                                                                                          month: 'short',
                                                                                          day: 'numeric',
                                                                                          year: 'numeric',
                                                                                      },
                                                                                  )
                                                                                : '—'}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {d.total_copies}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <Typography
                                                                                color="error"
                                                                                variant="body2"
                                                                                fontWeight="bold"
                                                                            >
                                                                                {
                                                                                    d.duplicates_to_archive
                                                                                }
                                                                            </Typography>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ),
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </>
                                        )}
                                    </Box>
                                )}
                            </Box>

                            <Typography variant="h6" gutterBottom>
                                Active Events
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Archive events to remove them from reports. Archived events can be
                                restored below.
                            </Typography>

                            {loading ? (
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress />
                                </Box>
                            ) : performances.length === 0 ? (
                                <Alert severity="info" sx={{ mb: 3 }}>
                                    No active events to display
                                </Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Athlete</TableCell>
                                                <TableCell>Event</TableCell>
                                                <TableCell>Result</TableCell>
                                                <TableCell>Meet</TableCell>
                                                <TableCell align="right">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[...performances]
                                                .sort(
                                                    (a, b) =>
                                                        new Date(a.meet_date).getTime() -
                                                        new Date(b.meet_date).getTime(),
                                                )
                                                .map((perf) => (
                                                    <TableRow key={perf.uuid} hover>
                                                        <TableCell>
                                                            {perf.meet_date
                                                                ? new Date(
                                                                      perf.meet_date,
                                                                  ).toLocaleDateString('en-US', {
                                                                      month: 'short',
                                                                      day: 'numeric',
                                                                      year: 'numeric',
                                                                  })
                                                                : '—'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {perf.individual?.name || 'Unknown'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {normalizeEventName(perf.event_type)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                fontWeight="bold"
                                                            >
                                                                {formatResult(
                                                                    perf.result_value,
                                                                    perf.result_unit,
                                                                )}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {perf.meet_name || '—'}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {confirmingArchive === perf.uuid ? (
                                                                <Box
                                                                    display="flex"
                                                                    alignItems="center"
                                                                    gap={0.5}
                                                                    justifyContent="flex-end"
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            mr: 0.5,
                                                                        }}
                                                                    >
                                                                        Archive?
                                                                    </Typography>
                                                                    <Button
                                                                        size="small"
                                                                        variant="contained"
                                                                        color="error"
                                                                        onClick={() =>
                                                                            handleSoftDelete(
                                                                                perf.uuid,
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            minWidth: 'auto',
                                                                            px: 1,
                                                                            py: 0.25,
                                                                            textTransform: 'none',
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        Yes
                                                                    </Button>
                                                                    <Button
                                                                        size="small"
                                                                        variant="text"
                                                                        onClick={() =>
                                                                            setConfirmingArchive(
                                                                                null,
                                                                            )
                                                                        }
                                                                        sx={{
                                                                            minWidth: 'auto',
                                                                            px: 1,
                                                                            py: 0.25,
                                                                            textTransform: 'none',
                                                                            fontSize: '0.75rem',
                                                                        }}
                                                                    >
                                                                        No
                                                                    </Button>
                                                                </Box>
                                                            ) : (
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() =>
                                                                        setConfirmingArchive(
                                                                            perf.uuid,
                                                                        )
                                                                    }
                                                                    color="error"
                                                                    title="Archive"
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Typography variant="h6" gutterBottom>
                                Archived Events
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                These events have been archived and are hidden from reports. Click
                                restore to bring them back.
                            </Typography>

                            {loadingArchived ? (
                                <Box display="flex" justifyContent="center" p={4}>
                                    <CircularProgress />
                                </Box>
                            ) : archivedEvents.length === 0 ? (
                                <Alert severity="info">No archived events</Alert>
                            ) : (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Date</TableCell>
                                                <TableCell>Athlete</TableCell>
                                                <TableCell>Event</TableCell>
                                                <TableCell>Result</TableCell>
                                                <TableCell>Meet</TableCell>
                                                <TableCell>Archived</TableCell>
                                                <TableCell align="right">Action</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {archivedEvents.map((perf) => (
                                                <TableRow
                                                    key={perf.uuid}
                                                    hover
                                                    sx={{ opacity: 0.7 }}
                                                >
                                                    <TableCell>
                                                        {perf.meet_date
                                                            ? new Date(
                                                                  perf.meet_date,
                                                              ).toLocaleDateString('en-US', {
                                                                  month: 'short',
                                                                  day: 'numeric',
                                                                  year: 'numeric',
                                                              })
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {perf.individual?.name || 'Unknown'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {normalizeEventName(perf.event_type)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            fontWeight="bold"
                                                        >
                                                            {formatResult(
                                                                perf.result_value,
                                                                perf.result_unit,
                                                            )}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>{perf.meet_name || '—'}</TableCell>
                                                    <TableCell>
                                                        {perf.archived_at
                                                            ? new Date(
                                                                  perf.archived_at,
                                                              ).toLocaleDateString('en-US', {
                                                                  month: 'short',
                                                                  day: 'numeric',
                                                                  year: 'numeric',
                                                              })
                                                            : '—'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Button
                                                            size="small"
                                                            startIcon={
                                                                <RestoreIcon fontSize="small" />
                                                            }
                                                            onClick={() => handleRestore(perf.uuid)}
                                                            color="success"
                                                            variant="outlined"
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            Restore
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </TabPanel>
                    )}
                </DialogContent>
            </Dialog>

            {/* Event Detail Drill-Down Dialog */}
            <Dialog
                open={eventDetailOpen}
                onClose={() => setEventDetailOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: glassStyle }}
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box display="flex" alignItems="center" gap={1.5}>
                            <IconButton onClick={() => setEventDetailOpen(false)} size="small">
                                <ArrowBackIcon />
                            </IconButton>
                            <DirectionsRunIcon
                                sx={{
                                    fontSize: 28,
                                    color: theme.palette.info.main,
                                }}
                            />
                            <Typography variant="h6">{eventDetailEvent}</Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            {eventDetailPR && (
                                <Chip
                                    icon={<EmojiEventsIcon />}
                                    label={`PR: ${formatResult(
                                        eventDetailPR.result,
                                        eventDetailPR.unit,
                                    )}`}
                                    color="success"
                                    size="small"
                                    sx={{ fontWeight: 600 }}
                                />
                            )}
                            <Chip
                                label={`${eventDetailData.length} performance${
                                    eventDetailData.length !== 1 ? 's' : ''
                                }`}
                                variant="outlined"
                                size="small"
                            />
                            <IconButton onClick={() => setEventDetailOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {eventDetailLoading ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : eventDetailData.length === 0 ? (
                        <Alert severity="info">No performances found for this event.</Alert>
                    ) : (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{
                                background: alpha(theme.palette.background.paper, 0.3),
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Meet</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                                        <TableCell sx={{ fontWeight: 700 }} align="right">
                                            Result
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 700 }}>Source</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {eventDetailData.map((perf: any) => {
                                        const isHighlighted = isPRRow(perf);
                                        return (
                                            <TableRow
                                                key={perf.uuid}
                                                sx={{
                                                    ...(isHighlighted
                                                        ? {
                                                              background: alpha(
                                                                  theme.palette.success.main,
                                                                  0.12,
                                                              ),
                                                              borderLeft: `4px solid ${theme.palette.success.main}`,
                                                              '& td': {
                                                                  fontWeight: 600,
                                                              },
                                                          }
                                                        : {}),
                                                    '&:hover': {
                                                        bgcolor: alpha(
                                                            theme.palette.action.hover,
                                                            0.08,
                                                        ),
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        {isHighlighted && (
                                                            <EmojiEventsIcon
                                                                sx={{
                                                                    fontSize: 18,
                                                                    color: theme.palette.success
                                                                        .main,
                                                                }}
                                                            />
                                                        )}
                                                        <Typography variant="body2">
                                                            {perf.meet_date
                                                                ? new Date(
                                                                      perf.meet_date,
                                                                  ).toLocaleDateString('en-US', {
                                                                      month: 'short',
                                                                      day: 'numeric',
                                                                      year: 'numeric',
                                                                  })
                                                                : '—'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {perf.meet_name || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {perf.meet_location || '—'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={
                                                            isHighlighted ? 'bold' : 'normal'
                                                        }
                                                        sx={
                                                            isHighlighted
                                                                ? {
                                                                      color: theme.palette.success
                                                                          .main,
                                                                  }
                                                                : {}
                                                        }
                                                    >
                                                        {formatResult(
                                                            perf.result_value,
                                                            perf.result_unit,
                                                        )}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    {perf.import_source &&
                                                    perf.import_source
                                                        .toLowerCase()
                                                        .includes('mile') ? (
                                                        <img
                                                            src="/milesplit-logo.png"
                                                            alt="MileSplit"
                                                            style={{
                                                                height: 28,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : perf.import_source &&
                                                      perf.import_source
                                                          .toLowerCase()
                                                          .includes('athletic') ? (
                                                        <img
                                                            src="/athletic-net-logo.png"
                                                            alt="Athletic.net"
                                                            style={{
                                                                height: 28,
                                                                objectFit: 'contain',
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            label={getSourceLabel(
                                                                perf.import_source,
                                                            )}
                                                            color={getSourceColor(
                                                                perf.import_source,
                                                            )}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '0.75rem' }}
                                                        />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default TrackEventViewer;
